/**
 * The voice control: one button out, three in.
 *
 * It lives in the top bar beside the help and chat buttons, which is where the
 * room's other always-available controls are. Three icon buttons is about 130px
 * — it fits at 320px, the width the seat-overflow work settled on as the floor.
 *
 * Every button says its **state and its action** in its label: "Microphone on,
 * tap to mute". An icon that toggles is exactly the control a screen reader
 * user cannot guess at, and an icon alone would leave them tapping to find out.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { browserVoiceSupport, joinVoice, voiceUnavailable } from '../net/voice.js';
import type { VoiceSession } from '../net/voice.js';
import { meshIsCrowded, sizeAfterJoining } from '../net/voice-roster.js';
import type { MatchView, VoiceMember } from '../net/match.js';
import type { IceServer } from '../net/session.js';
import { Icon } from '../render/Icon.js';

export interface VoiceControls {
  myId: string;
  iceServers: IceServer[];
  roster: Record<string, VoiceMember>;
  signal: (to: string, kind: 'offer' | 'answer' | 'candidate' | 'bye', payload: string) => void;
  onSignal: (handler: ((from: string, kind: string, payload: string) => void) | null) => void;
  setVoice: (state: { joined: boolean; micOff: boolean; earsOff: boolean }) => void;
}

export function VoiceButtons({
  view,
  controls,
}: {
  view: MatchView;
  controls: VoiceControls;
}) {
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [micOff, setMicOff] = useState(false);
  const [earsOff, setEarsOff] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  /*
   * The live session, for effects that must not re-run when it changes.
   *
   * Reconciling against the roster depends on the roster and on nothing else;
   * depending on the session object as well would tear down and rebuild the
   * subscription every time anything about the call changed.
   */
  const live = useRef<VoiceSession | null>(null);
  live.current = session;

  const joined = session !== null;
  const size = sizeAfterJoining(controls.roster, controls.myId);
  const crowded = meshIsCrowded(size);

  /** Tell the table what we are doing, whenever it changes. */
  const publish = useCallback(
    (state: { joined: boolean; micOff: boolean; earsOff: boolean }) => {
      controls.setVoice(state);
    },
    [controls],
  );

  /*
   * The roster is the trigger for everything.
   *
   * Not the join button, and not a message from anybody: when somebody appears
   * in it we call them, and when they disappear we hang up. That is what makes
   * arriving mid-conversation work without either end being told to expect the
   * other.
   */
  useEffect(() => {
    live.current?.sync(controls.roster);
  }, [controls.roster]);

  /** Blobs from peers go straight to the session that is expecting them. */
  useEffect(() => {
    controls.onSignal((from, kind, payload) => {
      live.current?.accept(from, kind, payload);
    });
    return () => controls.onSignal(null);
  }, [controls]);

  /*
   * Leaving the page leaves the conversation.
   *
   * Without this a closed tab holds its peer connections until the socket
   * notices, and everybody else keeps a dead connection open and a face in the
   * roster that cannot talk.
   */
  useEffect(() => {
    return () => {
      live.current?.leave();
      live.current = null;
    };
  }, []);

  const join = async () => {
    if (busy || joined) return;

    /*
     * Asked before the microphone is, because the answer is already known.
     *
     * An insecure origin has no `navigator.mediaDevices` at all, so the attempt
     * would throw and be reported as a missing permission — sending somebody to
     * a browser setting that cannot fix it.
     */
    const blocked = voiceUnavailable(browserVoiceSupport());
    if (blocked) {
      setProblem(blocked);
      return;
    }

    setBusy(true);
    setProblem(null);

    try {
      const next = await joinVoice({
        myId: controls.myId,
        iceServers: controls.iceServers,
        signal: controls.signal,
        onError: (message) => setProblem(message),
      });

      live.current = next;
      setSession(next);
      setMicOff(false);
      setEarsOff(false);
      publish({ joined: true, micOff: false, earsOff: false });
      // Connect to whoever is already talking, before the next roster arrives.
      next.sync(controls.roster);
    } catch {
      /*
       * Refused, or no microphone at all.
       *
       * An ordinary answer rather than an error — a party game that demands a
       * microphone has lost the room. The button goes back to how it was and
       * says what happened once.
       */
      setProblem('No microphone. Check the browser has permission.');
    } finally {
      setBusy(false);
    }
  };

  const leave = () => {
    session?.leave();
    live.current = null;
    setSession(null);
    setProblem(null);
    publish({ joined: false, micOff: false, earsOff: false });
  };

  const toggleMic = () => {
    const next = !micOff;
    setMicOff(next);
    session?.setMicOff(next);
    publish({ joined: true, micOff: next, earsOff });
  };

  const toggleEars = () => {
    const next = !earsOff;
    setEarsOff(next);
    session?.setEarsOff(next);
    publish({ joined: true, micOff, earsOff: next });
  };

  if (!joined) {
    return (
      <span className="voice">
        <button
          type="button"
          className="voice-join"
          onClick={() => void join()}
          disabled={busy}
          /*
           * The warning is in the label as well as on screen, because it is the
           * reason somebody might not press it and a screen reader user should
           * hear it before they do rather than after.
           */
          aria-label={
            crowded
              ? `Join audio. Voice may be choppy with ${String(size)} people.`
              : 'Join audio'
          }
          title={crowded ? 'Voice may be choppy with more than six people' : 'Join audio'}
        >
          {/*
            A mark AND the word.

            This was a bare microphone emoji in a 32px circle, which failed
            twice over: nothing said it was a button, and the one glyph it had
            is drawn differently by every operating system - on some of them a
            studio mic, on others a karaoke one. "Talk" is what you are about
            to do.
          */}
          <Icon name="mic" />
          <span>{busy ? 'Joining…' : 'Talk'}</span>
        </button>
        {problem ? <span className="voice-problem">{problem}</span> : null}
      </span>
    );
  }

  return (
    <span className="voice joined">
      {/*
        In a call, the three controls sit on one plate.

        They were three loose 32px circles that looked the same whether you
        were in a conversation or not. Grouped and lit, the strip itself is
        what says "you are talking to these people"; the buttons inside it only
        have to say which of the three they are.
      */}
      <button
        type="button"
        className={micOff ? 'voice-toggle off' : 'voice-toggle'}
        onClick={toggleMic}
        aria-pressed={!micOff}
        aria-label={micOff ? 'Microphone muted, tap to unmute' : 'Microphone on, tap to mute'}
      >
        <Icon name={micOff ? 'mic-off' : 'mic'} />
      </button>

      <button
        type="button"
        className={earsOff ? 'voice-toggle off' : 'voice-toggle'}
        onClick={toggleEars}
        aria-pressed={!earsOff}
        aria-label={earsOff ? 'Voices muted, tap to hear them' : 'Hearing voices, tap to mute'}
      >
        <Icon name={earsOff ? 'speaker-off' : 'speaker'} />
      </button>

      <button
        type="button"
        className="voice-toggle voice-leave"
        onClick={leave}
        aria-label="Leave audio"
      >
        <Icon name="close" />
      </button>

      {problem ? <span className="voice-problem">{problem}</span> : null}
      {view.lifecycle === 'playing' && crowded ? (
        <span className="voice-problem">{`${String(size)} people — may be choppy`}</span>
      ) : null}
    </span>
  );
}
